/**
 * PHASE CUTOFF UTILITIES (JavaScript version for API endpoints)
 * 
 * Centralized phase transition cutoff dates and utilities.
 * This is the SINGLE SOURCE OF TRUTH for determining which phase was active at any given timestamp.
 * 
 * CRITICAL: This module is used by API endpoints (verify-remitano-payment, verify-bsc-transaction)
 * to ensure users receive shares at the correct price based on when they SUBMITTED payment,
 * NOT when the payment was auto-approved.
 * 
 * All cutoff dates are in UTC (Supabase stores timestamps in UTC).
 * Phase transitions happen at 23:59:59 SAST (21:59:59 UTC) on the 5th of each month.
 */

/**
 * Phase transition cutoff dates.
 * Each date represents when the phase ENDED (exclusive upper bound).
 * Payments created BEFORE this date are in this phase.
 * 
 * IMPORTANT: Add new phase cutoffs here when phases transition!
 * Format: Phase end dates in UTC (ISO 8601)
 */
const PHASE_CUTOFF_DATES = {
 // Phase 0 (Pre Sale) @ $5/share - ended Nov 8, 2025
 0: '2025-11-08T07:53:27.602Z',

 // Phase 1 @ $10/share - ended Dec 6, 2025
 1: '2025-12-06T00:05:14.000Z',

 // Phase 2 @ $15/share - ended Jan 5, 2026 at 23:59:59 SAST (21:59:59 UTC)
 2: '2026-01-05T21:59:59.000Z',

 // Phase 3 @ $20/share - ended Feb 5, 2026 at 23:59:59 SAST (21:59:59 UTC)
 // CRITICAL: This is the Phase 3 Phase 4 transition cutoff
 // Payments created BEFORE this date should be approved at $20/share (Phase 3)
 // Payments created ON OR AFTER this date should be approved at $25/share (Phase 4)
 3: '2026-02-05T21:59:59.000Z',

 // Phase 4 @ $25/share - ended Mar 5, 2026 at 24:00 SAST (22:00 UTC)
 4: '2026-03-05T22:00:00.000Z',

 // Phase 5 @ $30/share - ended Apr 5, 2026 at 23:59:59 SAST (21:59:59 UTC)
 // CRITICAL: This is the Phase 5 → Phase 6 transition cutoff
 // Payments created BEFORE this date should be approved at $30/share (Phase 5)
 // Payments created ON OR AFTER this date should be approved at $35/share (Phase 6)
 5: '2026-04-05T21:59:59.000Z',

 // Phase 6 @ $35/share - ended May 5, 2026 at 23:59:39 SAST (21:59:39 UTC)
 // CRITICAL: This is the Phase 6 → Phase 7 transition cutoff
 // Value matches the actual DB end_date for phase_number=6 (2026-05-05T21:59:39.324Z)
 // Payments created BEFORE this date should be approved at $35/share (Phase 6)
 // Payments created ON OR AFTER this date should be approved at $40/share (Phase 7)
 6: '2026-05-05T21:59:39.324Z',


 // Phase 7 @ $40/share - ended Jun 5, 2026 at 22:00:10 UTC
 // CRITICAL: This is the Phase 7 → Phase 8 transition cutoff
 // Payments created BEFORE this date should be approved at $40/share (Phase 7)
 // Payments created ON OR AFTER this date should be approved at $45/share (Phase 8)
 7: '2026-06-05T22:00:10.339Z',
};

/**
 * Pricing grace period applied after every official phase boundary.
 * The new phase remains official immediately, but its higher price only becomes
 * effective after this grace window. During grace, pricing remains on the
 * previous phase.
 */
const PHASE_PRICING_GRACE_MINUTES = 60;
const PHASE_PRICING_GRACE_MS = PHASE_PRICING_GRACE_MINUTES * 60 * 1000;

/**
 * Phase pricing reference (for validation/logging purposes)
 */
const PHASE_PRICES = {
 0: 5.00, // Pre Sale
 1: 10.00, // Phase 1
 2: 15.00, // Phase 2
 3: 20.00, // Phase 3
 4: 25.00, // Phase 4
 5: 30.00, // Phase 5
 6: 35.00, // Phase 6
 7: 40.00, // Phase 7
 8: 45.00, // Phase 8
};

/**
 * Determine which phase was active at a given timestamp.
 *
 * CRITICAL FIX (Feb 9, 2026): The previous algorithm had a bug where it would
 * iterate from highest phase (19) down to lowest (0), and for phases without
 * cutoff dates (5-19), it would incorrectly return Phase 19 because all those
 * phases have no cutoff dates.
 *
 * NEW APPROACH: Only consider phases that either:
 * 1. Have a defined cutoff date in PHASE_CUTOFF_DATES, OR
 * 2. Are marked as is_active=true in the database
 *
 * @param {Date|string} timestamp - The timestamp to check (Date object or ISO string)
 * @param {Array} allPhases - Array of all investment phases from database
 * @returns {Object|null} The phase that was active at the given timestamp, or null if none found
 */
function getPhaseAtTimestamp(timestamp, allPhases = []) {
 const paymentDate = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

 if (!(paymentDate instanceof Date) || Number.isNaN(paymentDate.getTime())) {
 console.error('[phaseCutoffUtils] Invalid timestamp supplied:', timestamp);
 return null;
 }

 if (!Array.isArray(allPhases) || allPhases.length === 0) {
 console.error('[phaseCutoffUtils] No investment phases supplied');
 return null;
 }

 console.log('[phaseCutoffUtils] Determining phase for timestamp:', paymentDate.toISOString());

 const sortedPhases = [...allPhases].sort((a, b) => a.phase_number - b.phase_number);

 // Prefer authoritative DB phase windows when start_date/end_date are present.
 // Pricing windows are grace-adjusted: the new phase price starts 60 minutes
 // after the official boundary. The official boundary is the previous phase's
 // end_date when present, otherwise the new phase's start_date.
 const phasesWithDbDates = sortedPhases.filter(phase => phase.start_date || phase.end_date);
 if (phasesWithDbDates.length > 0) {
 for (let i = 0; i < sortedPhases.length; i++) {
 const phase = sortedPhases[i];
 const prevPhase = sortedPhases[i - 1];
 const nextPhase = sortedPhases[i + 1];
 const hasResolvableWindow = phase.start_date || phase.end_date || phase.is_active;

 if (!hasResolvableWindow) continue;

 const officialStart = resolveOfficialBoundary(prevPhase, phase);
 const officialEnd = resolveOfficialBoundary(phase, nextPhase);
 // Mine Production / PCAP (10.1) → Phase 11 must price at the official
 // 22:00 UTC boundary. The generic 60-minute grace would keep $100 pricing
 // until 22:59 after PCAP ends (#118).
 const skipGrace = isMineProductionPhase(phase) || isMineProductionPhase(prevPhase);
 const effectiveStart = prevPhase && officialStart
  ? (skipGrace ? officialStart : addPricingGrace(officialStart))
  : officialStart;
 const effectiveEnd = officialEnd
  ? (skipGrace ? officialEnd : addPricingGrace(officialEnd))
  : null;

 if (effectiveStart && paymentDate < effectiveStart) continue;
 if (effectiveEnd) {
  const pastEnd = skipGrace ? paymentDate > effectiveEnd : paymentDate >= effectiveEnd;
  if (pastEnd) continue;
 }

 console.log(`[phaseCutoffUtils] Matched grace-adjusted DB pricing window: ${phase.phase_name} @ $${phase.price_per_share}`);
 return phase;
 }
 }

 // Fallback to hardcoded cutoffs for older rows/environments where DB windows are absent.
 const phasesWithCutoffs = sortedPhases.filter(p => PHASE_CUTOFF_DATES[p.phase_number] !== undefined);
 console.log('[phaseCutoffUtils] Cutoff-backed phases:', phasesWithCutoffs.map(p => p.phase_name).join(', '));

 for (let i = 0; i < phasesWithCutoffs.length; i++) {
 const phase = phasesWithCutoffs[i];
 const prevPhase = phasesWithCutoffs[i - 1];
 const cutoffDate = parsePhaseDate(PHASE_CUTOFF_DATES[phase.phase_number]);
 const prevCutoffDate = prevPhase ? parsePhaseDate(PHASE_CUTOFF_DATES[prevPhase.phase_number]) : null;
 const effectiveCutoffDate = cutoffDate ? addPricingGrace(cutoffDate) : null;
 const effectivePrevCutoffDate = prevCutoffDate ? addPricingGrace(prevCutoffDate) : null;

 if (!effectiveCutoffDate) continue;
 if (effectivePrevCutoffDate && paymentDate < effectivePrevCutoffDate) continue;
 if (paymentDate < effectiveCutoffDate) {
 console.log(`[phaseCutoffUtils] Matched grace-adjusted cutoff phase: ${phase.phase_name} @ $${phase.price_per_share}`);
 return phase;
 }
 }

 const highestCutoffPhaseNumber = Math.max(...Object.keys(PHASE_CUTOFF_DATES).map(n => parseInt(n, 10)));
 const highestCutoff = parsePhaseDate(PHASE_CUTOFF_DATES[highestCutoffPhaseNumber]);
 const highestEffectiveCutoff = highestCutoff ? addPricingGrace(highestCutoff) : null;
 if (highestEffectiveCutoff && paymentDate >= highestEffectiveCutoff) {
 const nextPhase = sortedPhases.find(p => p.phase_number === highestCutoffPhaseNumber + 1);
 if (nextPhase && (nextPhase.is_active || nextPhase.start_date || PHASE_PRICES[nextPhase.phase_number] !== undefined)) {
 console.log(`[phaseCutoffUtils] Matched post-grace next phase: ${nextPhase.phase_name} @ $${nextPhase.price_per_share}`);
 return nextPhase;
 }
 }

 console.error('[phaseCutoffUtils] Could not determine phase for timestamp; failing closed');
 return null;
}

function parsePhaseDate(value) {
 if (!value) return null;
 const parsed = value instanceof Date ? value : new Date(value);
 return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function addPricingGrace(date) {
 return new Date(date.getTime() + PHASE_PRICING_GRACE_MS);
}

function isMineProductionPhase(phase) {
 if (!phase) return false;
 const num = Number(phase.phase_number);
 if (num === 10.1) return true;
 const name = String(phase.phase_name || '').toLowerCase();
 return name.includes('mine production');
}

function resolveOfficialBoundary(previousPhase, newPhase) {
 const previousEnd = parsePhaseDate(previousPhase?.end_date);
 if (previousEnd) return previousEnd;

 const newStart = parsePhaseDate(newPhase?.start_date);
 if (newStart) return newStart;

 return null;
}

/**
 * Validate that a phase price matches the expected price for that phase number.
 */
function validatePhasePrice(phase) {
 const expectedPrice = PHASE_PRICES[phase.phase_number];
 if (expectedPrice === undefined) return true;
 
 const actualPrice = parseFloat(String(phase.price_per_share));
 const tolerance = 0.01;
 
 return Math.abs(actualPrice - expectedPrice) <= tolerance;
}

/**
 * Get the expected price for a phase number.
 */
function getExpectedPhasePrice(phaseNumber) {
 return PHASE_PRICES[phaseNumber];
}

// ES6 named exports for TypeScript/frontend imports
export {
 PHASE_CUTOFF_DATES,
 PHASE_PRICES,
 PHASE_PRICING_GRACE_MINUTES,
 getPhaseAtTimestamp,
 validatePhasePrice,
 getExpectedPhasePrice,
 isMineProductionPhase
};

// CommonJS export for API endpoints (only in Node.js environment)
if (typeof module !== 'undefined' && module.exports) {
 module.exports = {
 PHASE_CUTOFF_DATES,
 PHASE_PRICES,
 PHASE_PRICING_GRACE_MINUTES,
 getPhaseAtTimestamp,
 validatePhasePrice,
 getExpectedPhasePrice,
 isMineProductionPhase
 };
}

